import { supabase } from '../supabase';
import { Tasker, TaskOffers, Tasks } from '../utils/types';

export const fetchTaskers = async (): Promise<Tasker[]> => {
  const { data, error } = await supabase.from('taskers').select('*');

  if (error) {
    console.error('Error fetching taskers:', error);
    throw error;
  }

  return data || [];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchCategories = async (): Promise<any> => {
  const { data, error } = await supabase.from('categories').select('*');
  if (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
  return data || [];
};

export const fetchTaskerById = async (id: number): Promise<Tasker | null> => {
  const { data, error } = await supabase.from('taskers').select('*').eq('id', id).single();

  if (error) {
    console.error('Error fetching tasker:', error);
    throw error;
  }

  return data;
};

export const fetchTaskOffersForTask = async (
  taskId: number
): Promise<{ pendingOffers: TaskOffers[]; activeOffers: TaskOffers[] }> => {
  const { data, error } = await supabase
    .from('task_offers')
    .select(
      `
      *,
      tasker:tasker_id (
        id,
        name,
        last_name,
        email,
        phone_number,
        university,
        career,
        carreer,
        university_year
      )
    `
    )
    .eq('task_id', taskId);

  if (error) {
    console.error('Error fetching task offers:', error);
    throw error;
  }

  const pendingOffers = data.filter((offer) => offer.status === 'pending');
  const activeOffers = data.filter((offer) => ['applied', 'assigned'].includes(offer.status));

  return { pendingOffers, activeOffers };
};

export const createTasker = async (tasker: Omit<Tasker, 'id'>): Promise<Tasker> => {
  const { data, error } = await supabase.from('taskers').insert(tasker).select().single();

  if (error) {
    console.error('Error creating tasker:', error);
    throw error;
  }

  return data as Tasker;
};

export const updateTasker = async (id: number, updates: Partial<Tasker>): Promise<Tasker> => {
  const { data, error } = await supabase
    .from('taskers')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating tasker:', error);
    throw error;
  }

  if (!data) {
    throw new Error('No data returned after updating tasker');
  }

  return data as Tasker;
};

export const deleteTasker = async (id: number): Promise<void> => {
  const { error } = await supabase.from('taskers').delete().eq('id', id);

  if (error) {
    console.error('Error deleting tasker:', error);
    throw error;
  }
};

interface TaskerRevenue {
  tasker_id: number;
  tasker_name: string;
  monthly_revenue: Array<{
    month: string;
    revenue: number;
  }>;
}

export const fetchTaskersRevenue = async (
  page: number = 1,
  pageSize: number = 20
): Promise<TaskerRevenue[]> => {
  const offset = (page - 1) * pageSize;

  const { data, error } = await supabase.rpc('get_taskers_revenue', {
    limit_param: pageSize,
    offset_param: offset,
  });

  if (error) {
    console.error('Error fetching taskers revenue:', error);
    throw error;
  }

  return data || [];
};

export const fetchTaskersWithPagination = async (
  page: number = 1,
  pageSize: number = 30,
  selectedCategories: number[] = [],
  applicationStatus: 'pending' | 'approved' | 'rejected' | 'not_rejected' | null = null
): Promise<{ taskers: Tasker[]; total: number }> => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('taskers')
    .select('*, taskers_categories(category_id)', { count: 'exact' });

  if (selectedCategories.length > 0) {
    const taskerIds = await supabase
      .from('taskers_categories')
      .select('tasker_id')
      .in('category_id', selectedCategories)
      .then(({ data }) => data?.map((item) => item.tasker_id) || []);

    query = query.in('id', taskerIds);
  }

  if (applicationStatus === 'not_rejected') {
    query = query.or('application_status.neq.rejected,application_status.is.null');
  } else if (applicationStatus) {
    query = query.eq('application_status', applicationStatus);
  }

  query = query.range(from, to).order('created_at', { ascending: false });

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching taskers:', error);
    throw error;
  }

  return { taskers: data || [], total: count || 0 };
};

export const rejectTasker = async (taskerId: number): Promise<void> => {
  const { error } = await supabase
    .from('taskers')
    .update({ is_approved: false, application_status: 'rejected' })
    .eq('id', taskerId);

  if (error) {
    console.error('Error rejecting tasker:', error);
    throw error;
  }
};

export const approveTasker = async (taskerId: number): Promise<void> => {
  const { error } = await supabase
    .from('taskers')
    .update({ is_approved: true, application_status: 'approved' })
    .eq('id', taskerId);

  if (error) {
    console.error('Error approving tasker:', error);
    throw error;
  }
};

export const fetchPendingTaskers = async (
  page: number = 1,
  pageSize: number = 30,
  selectedCategories: number[] = []
): Promise<{ taskers: Tasker[]; total: number }> => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('taskers')
    .select('*, taskers_categories!inner(*)', { count: 'exact' })
    .eq('application_status', 'pending')
    .not('certificate_regular_alumn_url', 'is', null)
    .not('certificate_criminal_record_url', 'is', null)
    .not('photo_selfie_url', 'is', null)
    .not('availability', 'is', null);

  if (selectedCategories.length > 0) {
    query = query.in('taskers_categories.category_id', selectedCategories);
  }

  query = query.range(from, to).order('created_at', { ascending: false });

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching pending taskers:', error);
    throw error;
  }

  return { taskers: data || [], total: count || 0 };
};

export const fetchPendingTaskersCount = async (): Promise<number> => {
  const { count, error } = await supabase
    .from('taskers')
    .select('*, taskers_categories!inner(*)', { count: 'exact', head: true })
    .neq('application_status', 'approved')
    .neq('application_status', 'rejected')
    .eq('profile_completed', true)
    .not('certificate_regular_alumn_url', 'is', null)
    .not('certificate_criminal_record_url', 'is', null)
    .not('photo_selfie_url', 'is', null);

  if (error) {
    console.error('Error fetching pending taskers count:', error);
    throw error;
  }

  return count || 0;
};

export const fetchTaskerByEmail = async (email: string) => {
  const { data, error } = await supabase.from('taskers').select('*').eq('email', email).single();

  if (error) throw error;
  return data;
};

export const checkTaskerHasBankAccount = async (taskerId: number) => {
  const { data, error } = await supabase
    .from('bank_accounts')
    .select('id')
    .eq('tasker_id', taskerId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
};

export const fetchTasksForTasker = async (taskerId: string): Promise<Tasks[]> => {
  try {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('tasker_id', taskerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tasks for tasker:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Unexpected error in fetchTasksForTasker:', error);
    throw error;
  }
};

export const fetchTaskerProfileData = async (taskerId: string) => {
  const { data: taskerData, error: taskerError } = await supabase
    .from('taskers')
    .select('*')
    .eq('id', taskerId)
    .single();

  if (taskerError) {
    console.error('Error fetching tasker:', taskerError);
    throw taskerError;
  }

  const { data: categoriesData, error: categoriesError } = await supabase
    .from('taskers_categories')
    .select('*, categories(id, name)')
    .eq('tasker_id', taskerId);

  if (categoriesError) {
    console.error('Error fetching tasker categories:', categoriesError);
    throw categoriesError;
  }

  const { data: reviewsData, error: reviewsError } = await supabase
    .from('tasks_reviews')
    .select('*, tasks(id, tasker_id)')
    .eq('tasks.tasker_id', taskerId);

  if (reviewsError) {
    console.error('Error fetching task reviews:', reviewsError);
    throw reviewsError;
  }

  const taskerTasks = await fetchTasksForTasker(taskerId);

  const { data: bankAccount, error: bankAccountError } = await supabase
    .from('bank_accounts')
    .select('*')
    .eq('tasker_id', taskerId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (bankAccountError) {
    console.error('Error fetching bank account:', bankAccountError);
  }

  return {
    tasker: taskerData,
    categories: categoriesData,
    reviews: reviewsData,
    tasks: taskerTasks,
    bankAccount,
  };
};

export const fetchPendingTaskersPaginated = async (
  page: number = 1,
  pageSize: number = 50,
  selectedCategories: number[] = []
): Promise<{ taskers: Tasker[]; total: number }> => {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('taskers')
    .select('*, taskers_categories!inner(*)', { count: 'exact' })
    .neq('application_status', 'approved')
    .neq('application_status', 'rejected')
    .eq('profile_completed', true)
    .not('certificate_regular_alumn_url', 'is', null)
    .not('certificate_criminal_record_url', 'is', null)
    .not('photo_selfie_url', 'is', null);

  if (selectedCategories.length > 0) {
    query = query.in('taskers_categories.category_id', selectedCategories);
  }

  query = query.range(from, to).order('created_at', { ascending: false });

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching pending taskers:', error);
    throw error;
  }

  return {
    taskers: data || [],
    total: count || 0,
  };
};

export const fetchAvailableTaskers = (categoryId: number, cityToFilter: string) => {
  return supabase.rpc('get_available_taskers', {
    category_id_param: categoryId,
    city_param: cityToFilter || null,
  });
};

export const fetchByCategory = (taskerIds: number[], categoryId: number) => {
  return supabase
    .from('taskers')
    .select(
      `
          auth_id,
          taskers_categories!inner(category_id)
        `
    )
    .in('id', taskerIds)
    .eq('taskers_categories.category_id', categoryId);
};

export const nonBlockedTaskers = (
  selectedCategories: number[],
  citiesInRegion: string[] | null = null
) => {
  let query = supabase
    .from('taskers')
    .select(
      `
        id,
        name,
        last_name,
        email,
        phone_number,
        city,
        taskers_categories!inner(category_id)
      `
    )
    .eq('application_status', 'approved')
    .not('is_blocked', 'is', true)
    .in('taskers_categories.category_id', selectedCategories);
  if (citiesInRegion) {
    query = query.in('city', citiesInRegion);
  }
  return query;
};

export const fetchOneTasker = (taskerId: number) => {
  return supabase.from('taskers').select('*').eq('id', taskerId).single();
};

export const fetchOneTaskerSelectAuthId = (taskerId: number) => {
  return supabase.from('taskers').select('auth_id').eq('id', taskerId).single();
};

export const updateTaskerBlocked = (taskerId: number, is_blocked: boolean) => {
  return supabase.from('taskers').update({ is_blocked }).eq('id', taskerId).select().single();
};

export const taskersCount = () => {
  return supabase
    .from('taskers')
    .select('id, name, last_name, email, phone_number, university, rut', {
      count: 'exact',
    });
};
